import {
    ActionIcon,
    Badge,
    Button,
    Code,
    Group,
    Loader,
    Modal,
    Select,
    Stack,
    Text,
    Tooltip,
} from '@mantine/core';
import {
    ArrowLeft,
    CaretLeft,
    CaretRight,
    DownloadSimple,
    PencilSimple,
    Plus,
    UploadSimple,
    Warning,
    X,
} from '@phosphor-icons/react';
import { useCallback, useEffect, useRef, useState, type FC } from 'react';

import type {
    ForecastChange,
    WarehouseDemandColumn,
    WarehouseDemandRow,
    WarehouseDemandSkuRow,
    WarehouseDemandSkuTableResponse,
    WarehouseDemandTableResponse,
    WhMixOverrideRow,
} from '@api/rebalance';
import {
    addSkuDemand,
    deleteWhMixOverrides,
    getWarehouseDemandSkuTable,
    getWarehouseDemandTable,
    upsertWhMixOverrides,
} from '@api/rebalance';
import { neutralColors } from '@styles/design-system';
import { showError, showSuccess } from '@utils/notifications';
import { formatCount } from '@utils/currency';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n: number, decimals = 0) =>
    formatCount(n, { decimals: decimals });

const pct = (n: number) => `${(n * 100).toFixed(1)}%`;

function shiftPeriod(periodStart: string, periodType: string, direction: 1 | -1): string {
    const d = new Date(`${periodStart}T12:00:00Z`);
    const pt = periodType.toUpperCase();
    if (pt === 'MONTH') d.setUTCMonth(d.getUTCMonth() + direction);
    else if (pt === 'WEEK') d.setUTCDate(d.getUTCDate() + direction * 7);
    else if (pt === 'QUARTER') d.setUTCMonth(d.getUTCMonth() + direction * 3);
    else d.setUTCDate(d.getUTCDate() + direction);
    return d.toISOString().slice(0, 10);
}

// ─── Shared table styles ──────────────────────────────────────────────────────

const cellStyle = {
    padding: '8px 12px',
    borderBottom: '1px solid rgba(0,0,0,0.06)',
    fontSize: 13,
    whiteSpace: 'nowrap' as const,
    verticalAlign: 'middle' as const,
};

const headerCellStyle = {
    ...cellStyle,
    fontWeight: 600,
    fontSize: 11,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    color: neutralColors.textMuted,
    backgroundColor: '#fafafa',
    borderBottom: '1px solid rgba(0,0,0,0.1)',
    position: 'sticky' as const,
    top: 0,
    zIndex: 1,
};

// ─── CSV helpers ──────────────────────────────────────────────────────────────

interface ParsedOverrideRow {
    productId: string;
    warehouseId: string;
    value: number;
}

/**
 * Parses a CSV with format: product_id, WH1, WH2, ...
 * Only WH columns present in the CSV are processed — partial uploads are valid.
 * No 100% sum constraint.
 */
function parseOverrideCsv(
    csv: string,
    columns: WarehouseDemandColumn[],
): { rows: ParsedOverrideRow[]; errors: string[] } {
    const lines = csv.trim().split('\n').map(l => l.split(',').map(c => c.trim()));
    if (lines.length < 2) return { rows: [], errors: ['CSV is empty or has no data rows'] };

    const header = lines[0];
    // First column is product_id; remaining columns are warehouse IDs
    const csvWhIds = header.slice(1);

    const knownWhIds = new Set(columns.map(c => c.warehouseId));
    const unknownWhs = csvWhIds.filter(id => id && !knownWhIds.has(id));
    const errors: string[] = unknownWhs.length > 0
        ? [`Unknown warehouse IDs in CSV (will be skipped): ${unknownWhs.join(', ')}`]
        : [];

    const result: ParsedOverrideRow[] = [];

    for (let i = 1; i < lines.length; i++) {
        const row = lines[i];
        if (!row.length || !row[0]) continue;
        const productId = row[0];
        if (productId.toUpperCase() === 'TOTAL') continue;

        for (let j = 1; j < row.length; j++) {
            const whId = csvWhIds[j - 1];
            if (!whId || !knownWhIds.has(whId)) continue;
            const raw = row[j];
            if (!raw || raw === '') continue; // blank = skip this WH (no override)
            const val = parseFloat(raw);
            if (isNaN(val)) continue;
            result.push({ productId, warehouseId: whId, value: val });
        }
    }

    return { rows: result, errors };
}

function downloadSampleCsv(skuRows: WarehouseDemandSkuRow[], columns: WarehouseDemandColumn[], channelId: string, periodLabel: string) {
    const whIds = columns.map(c => c.warehouseId);
    const header = ['product_id', ...whIds].join(',');
    const dataRows = skuRows.map(r =>
        [r.productId, ...whIds.map(wid => (r.warehouseDemand[wid] ?? 0).toFixed(2))].join(','),
    );
    const csv = [header, ...dataRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wh-demand-${channelId}-${periodLabel.replace(/\s/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
}

// ─── Forecast Change Warning ──────────────────────────────────────────────────

interface ForecastChangeWarningProps {
    opened: boolean;
    onClose: () => void;
    changes: ForecastChange[];
}

const ForecastChangeWarning: FC<ForecastChangeWarningProps> = ({ opened, onClose, changes }) => (
    <Modal
        opened={opened}
        onClose={onClose}
        title={<Text fw={600} size="sm">Channel Forecast Updated</Text>}
        size="md"
        radius="md"
        zIndex={500}
    >
        <Stack gap="sm">
            <Text size="sm" c="dimmed">
                Your WH overrides implied a different total for the following SKUs.
                The channel-level forecast has been updated to match.
            </Text>
            <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                        <tr>
                            <th style={{ ...headerCellStyle, textAlign: 'left' }}>Product ID</th>
                            <th style={{ ...headerCellStyle, textAlign: 'right' }}>Previous</th>
                            <th style={{ ...headerCellStyle, textAlign: 'right' }}>Updated To</th>
                        </tr>
                    </thead>
                    <tbody>
                        {changes.map(c => (
                            <tr key={`${c.productId}-${c.period}`}>
                                <td style={cellStyle}>{c.productId}</td>
                                <td style={{ ...cellStyle, textAlign: 'right', color: neutralColors.textMuted }}>
                                    {c.oldTotal !== null ? fmt(c.oldTotal, 1) : '—'}
                                </td>
                                <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 600 }}>
                                    {fmt(c.newTotal, 1)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Group justify="flex-end">
                <Button size="xs" onClick={onClose}>OK</Button>
            </Group>
        </Stack>
    </Modal>
);

// ─── CSV Upload Dialog ────────────────────────────────────────────────────────

interface CsvUploadDialogProps {
    opened: boolean;
    onClose: () => void;
    columns: WarehouseDemandColumn[];
    skuRows: WarehouseDemandSkuRow[];
    channelId: string;
    periodLabel: string;
    onUpload: (file: File) => void;
}

const CsvUploadDialog: FC<CsvUploadDialogProps> = ({
    opened, onClose, columns, skuRows, channelId, periodLabel, onUpload,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const whIds = columns.map(c => c.warehouseId);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) { onUpload(file); onClose(); }
        e.target.value = '';
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={<Text fw={600} size="sm">Upload WH Demand Override</Text>}
            size="lg"
            radius="md"
        >
            <Stack gap="md">
                <Text size="sm" c="dimmed">
                    Upload a CSV to override WH demand for <strong>{channelId}</strong> — <strong>{periodLabel}</strong>.
                    Enter absolute demand units per WH. Partial uploads are valid — only columns present will be updated.
                </Text>

                <div>
                    <Text size="xs" fw={600} mb={6} style={{ color: neutralColors.textPrimary }}>CSV Format</Text>
                    <Code block style={{ fontSize: 11, lineHeight: 1.6 }}>
                        {'product_id, ' + whIds.join(', ')}{'\n'}
                        {'prod-uuid-1, 480, 420, 300'}{'\n'}
                        {'prod-uuid-2, , 294, '}
                    </Code>
                    <Text size="xs" c="dimmed" mt={6}>
                        Blank cells are skipped — only columns you include are overridden.
                        Columns after <Code>product_id</Code> must match warehouse IDs exactly.
                    </Text>
                </div>

                <Group justify="space-between">
                    <Button
                        size="xs"
                        variant="light"
                        leftSection={<DownloadSimple size={13} />}
                        onClick={() => downloadSampleCsv(skuRows, columns, channelId, periodLabel)}
                    >
                        Download Template
                    </Button>
                    <Group gap={8}>
                        <Button size="xs" variant="subtle" onClick={onClose}>Cancel</Button>
                        <Button
                            size="xs"
                            leftSection={<UploadSimple size={13} />}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            Choose File
                        </Button>
                    </Group>
                </Group>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
            </Stack>
        </Modal>
    );
};

// ─── Add SKU modal ────────────────────────────────────────────────────────────

interface AddSkuModalProps {
    opened: boolean;
    onClose: () => void;
    columns: WarehouseDemandColumn[];
    channelId: string;
    periodStart: string;
    periodType: string;
    periodLabel: string;
    onSaved: () => void;
}

const AddSkuModal: FC<AddSkuModalProps> = ({
    opened, onClose, columns, channelId, periodStart, periodType, periodLabel, onSaved,
}) => {
    const [skuCode, setSkuCode] = useState('');
    const [whValues, setWhValues] = useState<Record<string, number>>({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const totalDemand = Math.round(Object.values(whValues).reduce((a, b) => a + b, 0));

    const handleClose = () => {
        setSkuCode('');
        setWhValues({});
        setError(null);
        onClose();
    };

    const handleConfirm = async () => {
        setError(null);
        if (!skuCode.trim()) { setError('SKU code is required'); return; }
        if (totalDemand <= 0) { setError('At least one warehouse must have demand > 0'); return; }

        setSaving(true);
        try {
            await addSkuDemand({
                channelId,
                periodStart,
                periodType,
                skuCode: skuCode.trim(),
                warehouseDemand: whValues,
            });
            showSuccess(`SKU "${skuCode}" added to ${channelId} — ${periodLabel}`);
            handleClose();
            onSaved();
        } catch (err: unknown) {
            const msg = (err as any)?.error?.data?.message;
            setError(typeof msg === 'string' ? msg : 'Failed to add SKU. Check the SKU code and try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={<Text fw={600} size="sm">Add SKU — {channelId} · {periodLabel}</Text>}
            size="lg"
            radius="md"
            zIndex={400}
        >
            <Stack gap="md">
                <div>
                    <Text size="xs" fw={600} mb={4} style={{ color: neutralColors.textPrimary }}>SKU Code</Text>
                    <input
                        type="text"
                        placeholder="e.g. SKU-001"
                        value={skuCode}
                        onChange={(e) => setSkuCode(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid rgba(0,0,0,0.15)',
                            borderRadius: 6,
                            fontSize: 13,
                            outline: 'none',
                            fontFamily: 'inherit',
                            boxSizing: 'border-box',
                        }}
                        onFocus={(e) => { e.target.style.borderColor = '#6366f1'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(0,0,0,0.15)'; }}
                    />
                </div>

                <div>
                    <Text size="xs" fw={600} mb={4} style={{ color: neutralColors.textPrimary }}>
                        Demand per Warehouse
                        {totalDemand > 0 && (
                            <Text span size="xs" c="dimmed" fw={400} ml={8}>
                                Total: {formatCount(totalDemand)} units
                            </Text>
                        )}
                    </Text>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ ...headerCellStyle, textAlign: 'left' }}>Warehouse</th>
                                <th style={{ ...headerCellStyle, textAlign: 'right' }}>Units</th>
                                <th style={{ ...headerCellStyle, textAlign: 'right' }}>Mix %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {columns.map((col) => {
                                const val = whValues[col.warehouseId] ?? 0;
                                const mix = totalDemand > 0 ? val / totalDemand : 0;
                                return (
                                    <tr key={col.warehouseId}>
                                        <td style={{ ...cellStyle, fontWeight: 500, color: neutralColors.textPrimary }}>
                                            {col.warehouseName}
                                        </td>
                                        <td style={{ ...cellStyle, padding: 0, textAlign: 'right' }}>
                                            <input
                                                type="number"
                                                className="wh-demand-input"
                                                min={0}
                                                step={1}
                                                value={val === 0 ? '' : val}
                                                placeholder="0"
                                                onChange={(e) => {
                                                    const v = Math.round(parseFloat(e.target.value) || 0);
                                                    setWhValues((prev) => ({ ...prev, [col.warehouseId]: v }));
                                                }}
                                                style={{
                                                    width: '100%',
                                                    border: 'none',
                                                    borderBottom: '2px solid transparent',
                                                    outline: 'none',
                                                    background: 'transparent',
                                                    textAlign: 'right',
                                                    fontSize: 13,
                                                    padding: '6px 12px',
                                                    fontFamily: 'inherit',
                                                    boxSizing: 'border-box',
                                                }}
                                                onFocus={(e) => { e.target.style.borderBottomColor = '#6366f1'; e.target.style.background = 'rgba(99,102,241,0.04)'; }}
                                                onBlur={(e) => { e.target.style.borderBottomColor = 'transparent'; e.target.style.background = 'transparent'; }}
                                            />
                                        </td>
                                        <td style={{ ...cellStyle, textAlign: 'right', color: neutralColors.textMuted, fontSize: 12 }}>
                                            {pct(mix)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {error && (
                    <Text size="xs" c="red">{error}</Text>
                )}

                <Group justify="flex-end" gap={8}>
                    <Button size="xs" variant="subtle" onClick={handleClose} disabled={saving}>Cancel</Button>
                    <Button size="xs" loading={saving} onClick={handleConfirm}>
                        Confirm &amp; Save
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

// ─── SKU drill-down table ─────────────────────────────────────────────────────

// editDraft[productId][warehouseId] = units (absolute demand)
type EditDraft = Record<string, Record<string, number>>;

interface SkuTableProps {
    data: WarehouseDemandSkuTableResponse;
    onBack: () => void;
    onSaved: () => void;
}

const SkuTable: FC<SkuTableProps> = ({ data, onBack, onSaved }) => {
    const [editMode, setEditMode] = useState(false);
    const [draft, setDraft] = useState<EditDraft>({});
    const [saving, setSaving] = useState(false);
    const [csvDialogOpen, setCsvDialogOpen] = useState(false);
    const [addSkuOpen, setAddSkuOpen] = useState(false);
    const [forecastChanges, setForecastChanges] = useState<ForecastChange[]>([]);

    const enterEditMode = () => {
        const initial: EditDraft = {};
        for (const row of data.rows) {
            initial[row.productId] = {};
            for (const col of data.columns) {
                initial[row.productId][col.warehouseId] = Math.round(row.warehouseDemand[col.warehouseId] ?? 0);
            }
        }
        setDraft(initial);
        setEditMode(true);
    };

    const cancelEditMode = () => {
        setDraft({});
        setEditMode(false);
    };

    const getDraftTotal = (productId: string): number =>
        Math.round(Object.values(draft[productId] ?? {}).reduce((a, b) => a + b, 0));

    const buildAndSaveRows = async (rowsToSave: WhMixOverrideRow[]) => {
        if (rowsToSave.length === 0) {
            showError('No non-zero warehouse values to save.');
            return false;
        }
        setSaving(true);
        try {
            const result = await upsertWhMixOverrides({
                channelId: data.channelId,
                periodStart: data.periodStart,
                periodType: data.periodType,
                rows: rowsToSave,
            });
            if (result.forecastChanges.length > 0) {
                setForecastChanges(result.forecastChanges);
            } else {
                showSuccess('Overrides saved.');
            }
            return true;
        } catch (err: unknown) {
            const msg = (err as any)?.error?.data?.message;
            showError(typeof msg === 'string' ? msg : 'Failed to save overrides.');
            return false;
        } finally {
            setSaving(false);
        }
    };

    const confirmOverrides = async () => {
        const overrideRows: WhMixOverrideRow[] = [];

        for (const [productId, whValues] of Object.entries(draft)) {
            for (const [warehouseId, units] of Object.entries(whValues)) {
                if (units > 0) {
                    overrideRows.push({ productId, warehouseId, value: units });
                }
            }
        }

        const saved = await buildAndSaveRows(overrideRows);
        if (saved) {
            setEditMode(false);
            setDraft({});
            onSaved();
        }
    };

    const handleCsvUpload = async (file: File) => {
        const text = await file.text();
        const { rows, errors } = parseOverrideCsv(text, data.columns);
        if (errors.length > 0 && rows.length === 0) {
            showError(`CSV errors:\n${errors.join('\n')}`);
            return;
        }
        if (errors.length > 0) {
            // Non-fatal warnings (e.g. unknown WH columns skipped)
            showError(`CSV warnings (continuing):\n${errors.join('\n')}`);
        }
        if (rows.length === 0) { showError('No valid rows found in CSV.'); return; }

        const overrideRows: WhMixOverrideRow[] = rows.map(r => ({
            productId: r.productId,
            warehouseId: r.warehouseId,
            value: r.value,
        }));

        setSaving(true);
        try {
            const result = await upsertWhMixOverrides({
                channelId: data.channelId,
                periodStart: data.periodStart,
                periodType: data.periodType,
                rows: overrideRows,
            });
            if (result.forecastChanges.length > 0) {
                setForecastChanges(result.forecastChanges);
            } else {
                showSuccess(`${overrideRows.length} override rows saved.`);
            }
            onSaved();
        } catch (err: unknown) {
            const msg = (err as any)?.error?.data?.message;
            showError(typeof msg === 'string' ? msg : 'Failed to save overrides.');
        } finally {
            setSaving(false);
        }
    };

    const handleClearSkuOverride = async (row: WarehouseDemandSkuRow) => {
        try {
            await deleteWhMixOverrides({
                channelId: data.channelId,
                periodStart: data.periodStart,
                productId: row.productId,
            });
            showSuccess('SKU override cleared.');
            onSaved();
        } catch {
            showError('Failed to clear SKU override.');
        }
    };

    return (
        <Stack gap={0}>
            {/* Hide number input spinners in edit mode */}
            <style>{`
                .wh-demand-input::-webkit-outer-spin-button,
                .wh-demand-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
                .wh-demand-input[type=number] { -moz-appearance: textfield; }
            `}</style>
            {/* Header bar */}
            <Group justify="space-between" mb="sm" wrap="nowrap">
                <Group gap={8} wrap="nowrap">
                    <ActionIcon variant="subtle" color="gray" onClick={onBack} disabled={editMode}>
                        <ArrowLeft size={16} />
                    </ActionIcon>
                    <div>
                        <Text style={{ fontWeight: 600, fontSize: 14, color: neutralColors.textPrimary }}>
                            {data.channelId} — {data.periodLabel}
                        </Text>
                        <Text style={{ fontSize: 12, color: neutralColors.textMuted }}>
                            {data.rows.length} SKUs
                        </Text>
                    </div>
                </Group>

                <Group gap={8}>
                    {editMode ? (
                        <>
                            <Button
                                size="xs"
                                variant="light"
                                leftSection={<UploadSimple size={13} />}
                                onClick={() => setCsvDialogOpen(true)}
                                disabled={saving}
                            >
                                Upload CSV
                            </Button>
                            <Button size="xs" variant="subtle" onClick={cancelEditMode} disabled={saving}>
                                Cancel
                            </Button>
                            <Button size="xs" loading={saving} onClick={confirmOverrides}>
                                Confirm Override
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                size="xs"
                                variant="subtle"
                                leftSection={<Plus size={13} />}
                                onClick={() => setAddSkuOpen(true)}
                            >
                                Add SKU
                            </Button>
                            <Button
                                size="xs"
                                variant="light"
                                leftSection={<PencilSimple size={13} />}
                                onClick={enterEditMode}
                            >
                                Edit
                            </Button>
                        </>
                    )}
                </Group>
            </Group>

            {/* Table */}
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(80vh - 200px)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={{ ...headerCellStyle, textAlign: 'left', minWidth: 160 }}>Product Name</th>
                            <th style={{ ...headerCellStyle, textAlign: 'right', minWidth: 100 }}>Total Demand</th>
                            {data.columns.map((col) => (
                                <th key={col.warehouseId} style={{ ...headerCellStyle, textAlign: 'right', minWidth: 110 }}>
                                    {col.warehouseName}
                                </th>
                            ))}
                            <th style={{ ...headerCellStyle, width: 40 }} />
                        </tr>
                    </thead>
                    <tbody>
                        {data.rows.map((row) => {
                            const isEditing = editMode;
                            const draftTotal = isEditing ? getDraftTotal(row.productId) : 0;

                            return (
                                <tr key={row.productId} style={{ backgroundColor: isEditing ? 'rgba(99,102,241,0.02)' : undefined }}>
                                    <td style={{ ...cellStyle, fontWeight: 500, color: neutralColors.textPrimary, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        <Group gap={6} wrap="nowrap">
                                            {row.productName}
                                            {row.hasOverride && !isEditing && (
                                                <Badge size="xs" color="indigo" variant="light">override</Badge>
                                            )}
                                        </Group>
                                        <Text size="xs" c="dimmed" ff="monospace">{row.skuCode}</Text>
                                    </td>
                                    <td style={{ ...cellStyle, textAlign: 'right', color: neutralColors.textPrimary }}>
                                        {isEditing ? (
                                            <Text style={{ fontSize: 13 }}>
                                                {fmt(draftTotal)}
                                                {draftTotal > 0 && (
                                                    <Text span style={{ fontSize: 10, color: neutralColors.textMuted, marginLeft: 4 }}>
                                                        (auto)
                                                    </Text>
                                                )}
                                            </Text>
                                        ) : (
                                            fmt(row.totalDemand)
                                        )}
                                    </td>
                                    {data.columns.map((col) => {
                                        if (isEditing) {
                                            const draftVal = draft[row.productId]?.[col.warehouseId] ?? 0;
                                            const draftMix = draftTotal > 0 ? draftVal / draftTotal : 0;
                                            return (
                                                <td key={col.warehouseId} style={{ ...cellStyle, padding: 0, textAlign: 'right' }}>
                                                    <input
                                                        type="number"
                                                        className="wh-demand-input"
                                                        min={0}
                                                        step={1}
                                                        value={draftVal === 0 ? '' : draftVal}
                                                        placeholder="0"
                                                        onChange={(e) => {
                                                            const v = Math.round(parseFloat(e.target.value) || 0);
                                                            setDraft((prev) => ({
                                                                ...prev,
                                                                [row.productId]: { ...prev[row.productId], [col.warehouseId]: v },
                                                            }));
                                                        }}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            border: 'none',
                                                            borderBottom: '2px solid transparent',
                                                            outline: 'none',
                                                            background: 'transparent',
                                                            textAlign: 'right',
                                                            fontSize: 13,
                                                            padding: '6px 12px 2px',
                                                            color: '#1a1a1a',
                                                            boxSizing: 'border-box',
                                                            fontFamily: 'inherit',
                                                        }}
                                                        onFocus={(e) => { e.target.style.borderBottomColor = '#6366f1'; e.target.style.background = 'rgba(99,102,241,0.04)'; }}
                                                        onBlur={(e) => { e.target.style.borderBottomColor = 'transparent'; e.target.style.background = 'transparent'; }}
                                                    />
                                                    <div style={{ fontSize: 10, color: neutralColors.textMuted, textAlign: 'right', padding: '0 12px 4px', lineHeight: 1 }}>
                                                        {pct(draftMix)}
                                                    </div>
                                                </td>
                                            );
                                        }
                                        const demand = row.warehouseDemand[col.warehouseId] ?? 0;
                                        const mix = row.warehouseMixPct[col.warehouseId] ?? 0;
                                        return (
                                            <td key={col.warehouseId} style={{ ...cellStyle, textAlign: 'right' }}>
                                                <div style={{ color: neutralColors.textPrimary }}>{fmt(demand)}</div>
                                                <div style={{ fontSize: 11, color: neutralColors.textMuted }}>{pct(mix)}</div>
                                            </td>
                                        );
                                    })}
                                    <td style={{ ...cellStyle, textAlign: 'right', padding: '4px 8px' }}>
                                        {!isEditing && row.hasOverride && (
                                            <Tooltip label="Clear override for this SKU" withArrow>
                                                <ActionIcon variant="subtle" color="red" size="sm" onClick={() => handleClearSkuOverride(row)}>
                                                    <X size={14} />
                                                </ActionIcon>
                                            </Tooltip>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <Text style={{ fontSize: 11, color: neutralColors.textMuted, marginTop: 8 }}>
                {editMode
                    ? 'Enter demand units per warehouse. Mix % is shown for reference. WHs left blank keep their calculated values.'
                    : 'Click Edit to override WH demand per SKU. Click Add SKU to add a new SKU to this channel\'s forecast.'}
            </Text>

            <CsvUploadDialog
                opened={csvDialogOpen}
                onClose={() => setCsvDialogOpen(false)}
                columns={data.columns}
                skuRows={data.rows}
                channelId={data.channelId}
                periodLabel={data.periodLabel}
                onUpload={handleCsvUpload}
            />

            <AddSkuModal
                opened={addSkuOpen}
                onClose={() => setAddSkuOpen(false)}
                columns={data.columns}
                channelId={data.channelId}
                periodStart={data.periodStart}
                periodType={data.periodType}
                periodLabel={data.periodLabel}
                onSaved={onSaved}
            />

            <ForecastChangeWarning
                opened={forecastChanges.length > 0}
                onClose={() => { setForecastChanges([]); }}
                changes={forecastChanges}
            />
        </Stack>
    );
};

// ─── Channel-level table ──────────────────────────────────────────────────────

interface ChannelTableProps {
    data: WarehouseDemandTableResponse;
    onRowClick: (row: WarehouseDemandRow) => void;
    onClearOverride: (row: WarehouseDemandRow) => void;
}

const ChannelTable: FC<ChannelTableProps> = ({ data, onRowClick, onClearOverride }) => (
    <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(80vh - 180px)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
                <tr>
                    <th style={{ ...headerCellStyle, textAlign: 'left', minWidth: 140 }}>Channel</th>
                    <th style={{ ...headerCellStyle, textAlign: 'right', minWidth: 110 }}>Total Demand</th>
                    {data.columns.map((col) => (
                        <th key={col.warehouseId} style={{ ...headerCellStyle, textAlign: 'right', minWidth: 110 }}>
                            {col.warehouseName}
                        </th>
                    ))}
                    <th style={{ ...headerCellStyle, width: 40 }} />
                </tr>
            </thead>
            <tbody>
                {data.rows.map((row) => (
                    <tr key={row.channelId} style={{ cursor: 'pointer' }} onClick={() => onRowClick(row)}>
                        <td style={{ ...cellStyle, color: neutralColors.textPrimary, fontWeight: 500 }}>
                            <Group gap={6} wrap="nowrap">
                                {row.channelId}
                                {row.hasOverride && <Badge size="xs" color="indigo" variant="light">override</Badge>}
                            </Group>
                        </td>
                        <td style={{ ...cellStyle, textAlign: 'right', color: neutralColors.textPrimary }}>
                            {fmt(row.totalDemand)}
                        </td>
                        {data.columns.map((col) => {
                            const demand = row.warehouseDemand[col.warehouseId] ?? 0;
                            const mix = row.warehouseMixPct[col.warehouseId] ?? 0;
                            return (
                                <td key={col.warehouseId} style={{ ...cellStyle, textAlign: 'right' }}>
                                    <div style={{ color: neutralColors.textPrimary }}>{fmt(demand)}</div>
                                    <div style={{ fontSize: 11, color: neutralColors.textMuted }}>{pct(mix)}</div>
                                </td>
                            );
                        })}
                        <td
                            style={{ ...cellStyle, textAlign: 'right', padding: '4px 8px' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {row.hasOverride && (
                                <Tooltip label="Clear overrides for this period" withArrow>
                                    <ActionIcon variant="subtle" color="red" size="sm" onClick={() => onClearOverride(row)}>
                                        <X size={14} />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

interface WarehouseDemandTabProps {
    initialChannelKey?: string;
}

type View = 'table' | 'sku';

const WarehouseDemandTab: FC<WarehouseDemandTabProps> = ({ initialChannelKey }) => {
    const [view, setView] = useState<View>('table');
    const [channelFilter, setChannelFilter] = useState<string | null>(initialChannelKey ?? null);

    const [tableData, setTableData] = useState<WarehouseDemandTableResponse | null>(null);
    const [tableLoading, setTableLoading] = useState(false);
    const [periodStart, setPeriodStart] = useState<string | null>(null);

    const [skuData, setSkuData] = useState<WarehouseDemandSkuTableResponse | null>(null);
    const [skuLoading, setSkuLoading] = useState(false);
    const [activeRow, setActiveRow] = useState<WarehouseDemandRow | null>(null);

    const loadTable = useCallback(async (channelId?: string, period?: string) => {
        setTableLoading(true);
        try {
            const data = await getWarehouseDemandTable({ channelId, periodStart: period });
            setTableData(data);
            if (!periodStart && data.rows.length > 0) setPeriodStart(data.rows[0].periodStart);
        } catch {
            showError('Failed to load warehouse demand data.');
        } finally {
            setTableLoading(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const reloadSku = useCallback(async (row: WarehouseDemandRow) => {
        setSkuLoading(true);
        try {
            const data = await getWarehouseDemandSkuTable({ channelId: row.channelId, periodStart: row.periodStart });
            setSkuData(data);
        } catch {
            showError('Failed to reload SKU data.');
        } finally {
            setSkuLoading(false);
        }
    }, []);

    useEffect(() => {
        loadTable(channelFilter ?? undefined, undefined);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePeriodShift = (direction: 1 | -1) => {
        if (!tableData || !periodStart) return;
        const next = shiftPeriod(periodStart, tableData.periodType, direction);
        setPeriodStart(next);
        loadTable(channelFilter ?? undefined, next);
    };

    const openSkuDrillDown = async (row: WarehouseDemandRow) => {
        setActiveRow(row);
        setSkuLoading(true);
        setView('sku');
        try {
            const data = await getWarehouseDemandSkuTable({ channelId: row.channelId, periodStart: row.periodStart });
            setSkuData(data);
        } catch {
            showError('Failed to load SKU demand data.');
        } finally {
            setSkuLoading(false);
        }
    };

    const handleClearChannelOverride = async (row: WarehouseDemandRow) => {
        try {
            await deleteWhMixOverrides({ channelId: row.channelId, periodStart: row.periodStart });
            showSuccess('Overrides cleared.');
            loadTable(channelFilter ?? undefined, periodStart ?? undefined);
        } catch {
            showError('Failed to clear overrides.');
        }
    };

    const channelOptions = tableData
        ? [...new Set(tableData.rows.map((r) => r.channelId))].map((id) => ({ value: id, label: id }))
        : [];

    // ── SKU view ──

    if (view === 'sku') {
        if (skuLoading) {
            return (
                <Stack align="center" justify="center" style={{ paddingTop: 60 }} gap={8}>
                    <Loader size="sm" />
                    <Text style={{ fontSize: 13, color: neutralColors.textMuted }}>Loading SKU demand…</Text>
                </Stack>
            );
        }
        if (!skuData || !activeRow) return null;
        return (
            <SkuTable
                data={skuData}
                onBack={() => { setView('table'); setSkuData(null); }}
                onSaved={() => reloadSku(activeRow)}
            />
        );
    }

    // ── Channel table view ──

    return (
        <Stack gap="md">
            <Group justify="space-between" wrap="nowrap">
                <Group gap={8}>
                    <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => handlePeriodShift(-1)} disabled={tableLoading}>
                        <CaretLeft size={14} />
                    </ActionIcon>
                    <Text style={{ fontSize: 13, fontWeight: 600, minWidth: 90, textAlign: 'center', color: neutralColors.textPrimary }}>
                        {tableData?.rows[0]?.periodLabel ?? periodStart ?? '…'}
                    </Text>
                    <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => handlePeriodShift(1)} disabled={tableLoading}>
                        <CaretRight size={14} />
                    </ActionIcon>
                </Group>
                <Select
                    placeholder="All channels"
                    data={channelOptions}
                    value={channelFilter}
                    onChange={(v) => { setChannelFilter(v); loadTable(v ?? undefined, periodStart ?? undefined); }}
                    size="xs"
                    clearable
                    style={{ width: 220 }}
                />
            </Group>

            {tableLoading ? (
                <Stack align="center" justify="center" style={{ paddingTop: 60 }} gap={8}>
                    <Loader size="sm" />
                    <Text style={{ fontSize: 13, color: neutralColors.textMuted }}>Loading…</Text>
                </Stack>
            ) : !tableData || tableData.rows.length === 0 ? (
                <Stack align="center" justify="center" style={{ paddingTop: 60 }} gap={8}>
                    <Warning size={28} color={neutralColors.textMuted} />
                    <Text style={{ fontSize: 14, color: neutralColors.textMuted }}>
                        No forecast data found. Upload a forecast to see warehouse demand.
                    </Text>
                </Stack>
            ) : (
                <ChannelTable
                    data={tableData}
                    onRowClick={openSkuDrillDown}
                    onClearOverride={handleClearChannelOverride}
                />
            )}

            <Text style={{ fontSize: 11, color: neutralColors.textMuted }}>
                Click a channel row to view SKU-level demand. Use Edit to override WH demand per SKU. Add SKU to add a new SKU to the channel forecast.
            </Text>
        </Stack>
    );
};

export default WarehouseDemandTab;
